import React, { useState, useEffect } from 'react'
import { Settings, Shield, Database, Info, Save, RotateCcw, Key, Radio, AlertTriangle, RefreshCw } from 'lucide-react'
import { useSettings } from '../context/SettingsContext'
import { schwab, dataSource, systemConfig, tradeTest, streaming } from '../lib/api'

// ── Section wrapper ────────────────────────────────────────────────────────────
function Section({ title, icon: Icon, children }) {
  return (
    <div className="rounded-xl border border-gray-800 p-5" style={{ background: '#111827' }}>
      <div className="flex items-center gap-2.5 mb-5 pb-4" style={{ borderBottom: '1px solid #1f2937' }}>
        <Icon className="h-4 w-4 text-slate-600" />
        <h3
          className="text-slate-500 uppercase font-semibold"
          style={{ fontSize: '0.7rem', letterSpacing: '0.12em' }}
        >
          {title}
        </h3>
      </div>
      <div className="space-y-5">{children}</div>
    </div>
  )
}

// ── Toggle ─────────────────────────────────────────────────────────────────────
function Toggle({ label, desc, checked, onChange }) {
  return (
    <label className="flex items-start justify-between gap-4 cursor-pointer">
      <div>
        <p className="text-sm text-slate-300 font-medium">{label}</p>
        {desc && <p className="text-slate-600 mt-0.5" style={{ fontSize: '0.68rem' }}>{desc}</p>}
      </div>
      <div
        onClick={() => onChange(!checked)}
        className="relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-all cursor-pointer"
        style={{
          background: checked ? '#1d4ed8' : '#1f2937',
          border: checked ? '1px solid rgba(59,130,246,0.5)' : '1px solid #374151',
          boxShadow: checked ? '0 0 8px rgba(59,130,246,0.3)' : 'none',
        }}
      >
        <span
          className="inline-block h-3.5 w-3.5 transform rounded-full transition-transform"
          style={{
            background: checked ? '#93c5fd' : '#475569',
            transform: checked ? 'translateX(18px)' : 'translateX(3px)',
          }}
        />
      </div>
    </label>
  )
}

// ── Slider ─────────────────────────────────────────────────────────────────────
function Slider({ label, desc, value, min, max, step = 0.05, fmt, onChange }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div>
          <p className="text-sm text-slate-300 font-medium">{label}</p>
          {desc && <p className="text-slate-600 mt-0.5" style={{ fontSize: '0.68rem' }}>{desc}</p>}
        </div>
        <span
          className="font-bold tabular-nums"
          style={{ color: '#22d3ee', fontFamily: 'JetBrains Mono, monospace', fontSize: '0.85rem' }}
        >
          {fmt ? fmt(value) : value}
        </span>
      </div>
      <input
        type="range"
        min={min} max={max} step={step}
        value={value}
        onChange={e => onChange(Number(e.target.value))}
        className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
        style={{
          background: `linear-gradient(to right, #22d3ee ${((value - min) / (max - min)) * 100}%, #1e293b ${((value - min) / (max - min)) * 100}%)`,
          accentColor: '#22d3ee',
        }}
      />
      <div className="flex justify-between text-slate-700 mt-1" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.62rem' }}>
        <span>{fmt ? fmt(min) : min}</span>
        <span>{fmt ? fmt(max) : max}</span>
      </div>
    </div>
  )
}

// ── Select ─────────────────────────────────────────────────────────────────────
function Select({ label, desc, value, options, onChange }) {
  return (
    <div>
      <p className="text-sm text-slate-300 font-medium mb-1">{label}</p>
      {desc && <p className="text-slate-600 mb-2" style={{ fontSize: '0.68rem' }}>{desc}</p>}
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full rounded-lg px-3 py-2 text-sm focus:outline-none"
        style={{
          background: '#0f172a',
          border: '1px solid #374151',
          color: '#94a3b8',
          fontFamily: 'JetBrains Mono, monospace',
          fontSize: '0.75rem',
        }}
      >
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  )
}

// ── Data Source Control ────────────────────────────────────────────────────────
function DataSourceSection() {
  const [source,     setSource]     = useState(null)
  const [switching,  setSwitching]  = useState(false)
  const [wsStatus,   setWsStatus]   = useState(null)
  const [msg,        setMsg]        = useState('')

  useEffect(() => {
    dataSource.get().then(d => { if (d.success) setSource(d.active_source) }).catch(() => {})
    streaming.status().then(setWsStatus).catch(() => {})
  }, [])

  const handleSwitch = async (target) => {
    if (switching || source === target) return
    setSwitching(true)
    setMsg('')
    try {
      const res = await dataSource.set(target)
      if (res.success) {
        setSource(res.active_source)
        setMsg(`Switched to ${res.active_source}. Change takes effect within 30s.`)
      } else {
        setMsg(res.error || 'Switch failed')
      }
    } catch { setMsg('Could not reach backend') }
    setSwitching(false)
  }

  const wsColor = wsStatus?.status === 'ACTIVE' ? '#10b981'
    : wsStatus?.status === 'DEGRADED' ? '#f59e0b'
    : wsStatus?.status === 'FAILED'   ? '#ef4444'
    : '#475569'

  return (
    <Section title="Data Source" icon={Radio}>
      <p className="text-slate-600 -mt-2 pb-3" style={{ fontSize: '0.68rem', borderBottom: '1px solid #1e293b' }}>
        Controls whether market data comes from Schwab WebSocket streaming or Alpaca REST polling.
        WebSocket gives 1-min fresh bars for all symbols simultaneously.
        REST rotates through 5 groups — each symbol refreshed every ~5 min.
      </p>

      <div className="flex gap-3 mb-4">
        {['schwab', 'alpaca'].map(src => {
          const isActive = source === src
          const isSchwab = src === 'schwab'
          return (
            <button
              key={src}
              onClick={() => handleSwitch(src)}
              disabled={switching}
              style={{
                flex: 1,
                padding: '10px 16px',
                borderRadius: '0.5rem',
                border: isActive ? `2px solid ${isSchwab ? '#6366f1' : '#22d3ee'}` : '2px solid #1e293b',
                background: isActive ? (isSchwab ? 'rgba(99,102,241,0.12)' : 'rgba(34,211,238,0.08)') : '#0f172a',
                color: isActive ? (isSchwab ? '#a5b4fc' : '#67e8f9') : '#475569',
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: '0.78rem',
                fontWeight: 700,
                cursor: switching ? 'not-allowed' : 'pointer',
                letterSpacing: '0.1em',
                transition: 'all 0.15s',
              }}
            >
              {switching && !isActive ? '...' : src.toUpperCase()}
              <div style={{ fontSize: '0.6rem', fontWeight: 400, marginTop: '3px', color: isActive ? 'inherit' : '#334155' }}>
                {isSchwab ? 'WebSocket · all symbols · 1-min' : 'REST polling · 5 groups · 5-min rotation'}
              </div>
            </button>
          )
        })}
      </div>

      {/* WebSocket health */}
      {source === 'schwab' && wsStatus && (
        <div style={{
          padding: '10px 14px',
          borderRadius: '0.5rem',
          background: '#0f172a',
          border: `1px solid ${wsColor}44`,
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          marginBottom: '8px',
        }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: wsColor, flexShrink: 0,
            boxShadow: wsStatus?.status === 'ACTIVE' ? `0 0 6px ${wsColor}` : 'none' }} />
          <div style={{ flex: 1 }}>
            <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.72rem', color: wsColor, fontWeight: 600 }}>
              WEBSOCKET {wsStatus?.status || 'UNKNOWN'}
            </span>
            {wsStatus?.seconds_since_tick != null && (
              <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.65rem', color: '#475569', marginLeft: '10px' }}>
                last tick {wsStatus.seconds_since_tick < 60
                  ? `${wsStatus.seconds_since_tick}s ago`
                  : `${Math.round(wsStatus.seconds_since_tick / 60)}m ago`}
              </span>
            )}
          </div>
          {wsStatus?.status === 'FAILED' && (
            <AlertTriangle style={{ width: 14, height: 14, color: '#ef4444', flexShrink: 0 }} />
          )}
        </div>
      )}

      {msg && (
        <p style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.68rem',
          color: msg.includes('failed') || msg.includes('Could not') ? '#f87171' : '#34d399',
          marginTop: '4px' }}>
          {msg}
        </p>
      )}
    </Section>
  )
}

// ── Trade Flow Test ────────────────────────────────────────────────────────────
function TradeFlowTestSection() {
  const [symbol,  setSymbol]  = useState('SPY')
  const [running, setRunning] = useState(false)
  const [result,  setResult]  = useState(null)
  const [error,   setError]   = useState(null)

  async function runTest() {
    const sym = symbol.trim().toUpperCase()
    if (!sym || !/^[A-Z0-9]{1,10}$/.test(sym)) {
      setError('Enter a valid ticker (letters/digits, up to 10 chars)')
      return
    }
    setRunning(true)
    setResult(null)
    setError(null)
    try {
      const res = await tradeTest.run(sym)
      setResult(res)
    } catch (e) {
      setError(e.message || 'Request failed')
    } finally {
      setRunning(false)
    }
  }

  const stepColor = status =>
    status === 'ok'   ? { color: '#10b981' } :
    status === 'fail' ? { color: '#ef4444' } :
                        { color: '#94a3b8' }

  const stepIcon = status =>
    status === 'ok'   ? '✓' :
    status === 'fail' ? '✗' : '•'

  return (
    <Section title="Trade Flow Test" icon={Radio}>
      <p className="text-slate-600 -mt-2 pb-3" style={{ fontSize: '0.68rem', borderBottom: '1px solid #1e293b' }}>
        Places a live paper-trade BUY then SELL (1 share) in Alpaca to verify the full
        data→order pipeline. No position tracking written. Orders use DAY time-in-force.
      </p>

      <div className="flex gap-2 items-end">
        <div className="flex-1">
          <p className="text-sm text-slate-300 font-medium mb-1">Symbol</p>
          <input
            type="text"
            value={symbol}
            onChange={e => setSymbol(e.target.value.toUpperCase().slice(0, 10))}
            placeholder="SPY"
            disabled={running}
            className="w-full rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none placeholder-slate-700 disabled:opacity-50"
            style={{
              background:  '#0f172a',
              border:      '1px solid #374151',
              fontFamily:  'JetBrains Mono, monospace',
              letterSpacing: '0.06em',
            }}
          />
        </div>
        <button
          onClick={runTest}
          disabled={running}
          className="px-4 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-40"
          style={{
            background:  running ? 'rgba(59,130,246,0.1)' : '#1d4ed8',
            color:       '#bfdbfe',
            border:      '1px solid rgba(59,130,246,0.4)',
            fontFamily:  'JetBrains Mono, monospace',
            fontSize:    '0.72rem',
            whiteSpace:  'nowrap',
          }}
        >
          {running ? 'RUNNING…' : 'RUN TEST'}
        </button>
      </div>

      {running && (
        <div
          className="rounded-lg p-3"
          style={{ background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.15)' }}
        >
          <p className="text-blue-400 animate-pulse" style={{ fontSize: '0.72rem', fontFamily: 'JetBrains Mono, monospace' }}>
            Placing orders… this takes up to ~25 seconds
          </p>
        </div>
      )}

      {error && !running && (
        <div
          className="rounded-lg p-3"
          style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)' }}
        >
          <p className="text-red-400" style={{ fontSize: '0.72rem', fontFamily: 'JetBrains Mono, monospace' }}>{error}</p>
        </div>
      )}

      {result && !running && (
        <div
          className="rounded-lg p-3 space-y-1.5"
          style={{
            background: result.success ? 'rgba(16,185,129,0.04)' : 'rgba(239,68,68,0.04)',
            border: result.success ? '1px solid rgba(16,185,129,0.2)' : '1px solid rgba(239,68,68,0.2)',
          }}
        >
          <p
            className="font-bold mb-2"
            style={{
              fontFamily: 'JetBrains Mono, monospace',
              fontSize:   '0.72rem',
              color:      result.success ? '#10b981' : '#ef4444',
            }}
          >
            {result.success ? '✓ ALL STEPS PASSED' : '✗ TEST FAILED'} — {result.symbol}
          </p>

          {(result.steps || []).map((s, i) => (
            <div key={i} className="flex gap-2 items-baseline">
              <span
                className="w-3 text-center flex-shrink-0 font-bold"
                style={{ ...stepColor(s.status), fontFamily: 'JetBrains Mono, monospace', fontSize: '0.75rem' }}
              >
                {stepIcon(s.status)}
              </span>
              <div>
                <span
                  className="font-semibold"
                  style={{ ...stepColor(s.status), fontFamily: 'JetBrains Mono, monospace', fontSize: '0.7rem' }}
                >
                  {s.step}
                </span>
                <span className="text-slate-500 ml-2" style={{ fontSize: '0.67rem' }}>
                  {s.detail}
                </span>
              </div>
            </div>
          ))}

          {result.fill_price && result.sell_price && (
            <div className="mt-2 pt-2" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
              <p className="text-slate-500" style={{ fontSize: '0.67rem', fontFamily: 'JetBrains Mono, monospace' }}>
                BUY ${result.fill_price.toFixed(2)} → SELL ${result.sell_price.toFixed(2)}
                {' '}&nbsp;|&nbsp;{' '}
                P&L ${(result.sell_price - result.fill_price).toFixed(4)}/share
                {result.db_age_min !== null && result.db_age_min !== undefined && (
                  <> &nbsp;|&nbsp; DB data {result.db_age_min}min old</>
                )}
              </p>
            </div>
          )}
        </div>
      )}
    </Section>
  )
}

// ── Live System Configuration ──────────────────────────────────────────────────
function LiveSystemConfigSection() {
  const [cfg,          setCfg]          = useState(null)
  const [loading,      setLoading]      = useState(true)
  const [capEdit,      setCapEdit]      = useState('')
  const [bpEdit,       setBpEdit]       = useState('')
  const [cfgSaving,    setCfgSaving]    = useState(false)
  const [cfgMsg,       setCfgMsg]       = useState(null)

  useEffect(() => {
    systemConfig.get()
      .then(d => { if (d.success) setCfg(d) })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const ConfigTile = ({ label, value, highlight }) => (
    <div className="rounded-lg p-3" style={{ background: '#0f172a', border: `1px solid ${highlight ? 'rgba(99,102,241,0.3)' : '#1e293b'}` }}>
      <p className="text-slate-600" style={{ fontSize: '0.6rem', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{label}</p>
      <p className="font-semibold mt-1" style={{
        fontFamily: 'JetBrains Mono, monospace', fontSize: '0.75rem',
        color: highlight ? '#a5b4fc' : '#cbd5e1',
      }}>{value}</p>
    </div>
  )

  if (loading) return (
    <Section title="System Configuration" icon={Info}>
      <div className="flex items-center gap-2 text-slate-600" style={{ fontSize: '0.72rem' }}>
        <RefreshCw className="h-3 w-3 animate-spin" /> Loading live values...
      </div>
    </Section>
  )

  if (!cfg) return (
    <Section title="System Configuration" icon={Info}>
      <p className="text-red-500" style={{ fontSize: '0.72rem', fontFamily: 'JetBrains Mono, monospace' }}>
        ✗ Could not load config from backend — API call failed or session expired.
      </p>
    </Section>
  )

  const sg = cfg.signal_generation || {}
  const ex = cfg.execution || {}
  const dc = cfg.data_collection || {}
  const rt = cfg.runtime || {}

  return (
    <Section title="System Configuration" icon={Info}>
      <p className="text-slate-600 -mt-2 pb-3" style={{ fontSize: '0.68rem', borderBottom: '1px solid #1e293b' }}>
        Live values from the running backend ({sg.signal_version || 'unknown version'}). Signal generation · execution · data collection.
      </p>

      <p className="text-slate-500 pb-2" style={{ fontSize: '0.62rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Signal Generation</p>
      <div className="grid grid-cols-2 gap-2 mb-4">
        <ConfigTile label="Timeframe"          value={sg.timeframe || '15-min bars'} highlight />
        <ConfigTile label="BUY Signals"        value={sg.buy_signals || '—'} />
        <ConfigTile label="SELL Signals"       value={sg.sell_signals || '—'} />
        <ConfigTile label="MFI"                value={`${sg.mfi_period ?? 14}-period, cross ${sg.mfi_cross_levels ?? '20/80'}`} />
        <ConfigTile label="Stochastic"         value={`${sg.stoch_period ?? '14,3'}, cross ${sg.stoch_cross_levels ?? '20/80'}`} />
        <ConfigTile label="Base Confidence"    value={sg.base_confidence || '—'} highlight />
        <ConfigTile label="Min Entry RSI"      value={sg.min_entry_rsi || '—'} />
      </div>

      <p className="text-slate-500 pb-2" style={{ fontSize: '0.62rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Execution & Risk</p>
      <div className="grid grid-cols-2 gap-2 mb-4">
        <ConfigTile label="Phase A Stop"       value={`${ex.initial_stop_pct ?? 2.5}% fixed (first ${ex.phase_a_minutes ?? 60}min)`} highlight />
        <ConfigTile label="Phase B Stop"       value={ex.trailing_stop_pct || 'ATR-based'} highlight />
        <ConfigTile label="Disaster Stop"      value={`${ex.disaster_stop_pct ?? 6.0}% Alpaca hard stop`} />
        <ConfigTile label="Min Profit to Sell" value={`${ex.min_profit_to_sell ?? 0.8}%`} />
        <ConfigTile label="Min Confidence"     value={`${ex.min_confidence ?? 30}%`} />
        <ConfigTile label="BUY Cooldown"       value={`${ex.buy_cooldown_min ?? 5} min`} />
        <ConfigTile label="Post-Loss Cooldown" value={`${ex.post_loss_cooldown ?? 10} min`} />
        <ConfigTile label="Position Size Cap"  value={`${ex.position_size_cap_pct ?? 5.0}% of gross portfolio`} />
        <ConfigTile label="Min Buying Power"   value={`${ex.min_buying_power_pct ?? 10.0}% floor (RED tier blocks BUYs)`} />
        <ConfigTile label="Max Per Sector"     value={`${ex.sector_max_positions ?? 8}`} />
      </div>
      {/* Max Risk/Trade, Max Portfolio Risk, and the "Max Positions" runtime tile below were
          removed 2026-08-10 -- confirmed via grep that sf_signal_scanner.py never reads
          max_risk_per_trade/max_portfolio_risk, and the count-based max_positions gate was
          explicitly replaced by the capital-tier system (see sf_signal_scanner.py's Capital
          Deployment Tier Gate comment). These were displaying numbers nobody could act on. */}

      <p className="text-slate-500 pb-2" style={{ fontSize: '0.62rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Data Collection</p>
      <div className="grid grid-cols-2 gap-2 mb-4">
        <ConfigTile label="Active Source"      value={(dc.active_source || 'schwab').toUpperCase()} highlight />
        <ConfigTile label="WebSocket Status"   value={dc.websocket_status || 'IDLE'} highlight />
        <ConfigTile label="Collection"         value={dc.collection_interval || 'Every 1 min'} />
        <ConfigTile label="Rotation"           value={`${dc.rotation_groups ?? 4} groups, full rotation ${dc.full_rotation_min ?? 4} min`} />
        <ConfigTile label="Active Watchlist"   value={dc.active_watchlist_symbols != null ? `${dc.active_watchlist_symbols} symbols` : '—'} />
        <ConfigTile label="Lookback Bars"      value={`${dc.lookback_bars ?? 500} 1-min bars`} />
      </div>

      <p className="text-slate-500 pb-2" style={{ fontSize: '0.62rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Runtime</p>
      <div className="grid grid-cols-2 gap-2">
        <ConfigTile label="Trading Mode"       value={rt.trading_mode || 'Paper Trading'} />
        <ConfigTile label="Trading Enabled"    value={rt.trading_enabled ? 'YES' : 'HALTED'} highlight={!rt.trading_enabled} />
        <ConfigTile label="Daily ST Gate"      value={rt.daily_st_gate || 'Disabled'} />
        <ConfigTile label="Daily SMA Gate"     value={rt.daily_sma_gate || 'Disabled'} />
        <ConfigTile label="SPY Bear Block"     value={rt.spy_bear_block ? 'Active' : 'Disabled'} />
        <ConfigTile label="VXX Halt"           value={rt.vxx_halt_threshold || 'Disabled'} />
        <ConfigTile label="VXX High Vol"       value={`> ${rt.vxx_high_threshold ?? 28} = half size`} />
      </div>

      {/* Capital controls live editor */}
      <div className="mt-4 pt-4" style={{ borderTop: '1px solid #1e293b' }}>
        <p className="text-slate-500 pb-3" style={{ fontSize: '0.62rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Capital Controls</p>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <p className="text-slate-600 mb-1" style={{ fontSize: '0.68rem' }}>
              Position Size Cap (% of gross portfolio)
              <br/>Current: <span style={{ color: '#a5b4fc', fontFamily: 'JetBrains Mono, monospace' }}>{ex.position_size_cap_pct ?? 5.0}%</span>
            </p>
            <input
              type="number"
              min={1} max={10} step={0.5}
              value={capEdit}
              onChange={e => setCapEdit(e.target.value)}
              placeholder={String(ex.position_size_cap_pct ?? 5.0)}
              disabled={cfgSaving}
              className="w-full rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none placeholder-slate-700 disabled:opacity-50"
              style={{ background: '#0f172a', border: '1px solid #374151', fontFamily: 'JetBrains Mono, monospace' }}
            />
          </div>
          <div>
            <p className="text-slate-600 mb-1" style={{ fontSize: '0.68rem' }}>
              Min Buying Power Floor (% — RED tier blocks BUYs)
              <br/>Current: <span style={{ color: '#a5b4fc', fontFamily: 'JetBrains Mono, monospace' }}>{ex.min_buying_power_pct ?? 10.0}%</span>
            </p>
            <input
              type="number"
              min={5} max={50} step={1}
              value={bpEdit}
              onChange={e => setBpEdit(e.target.value)}
              placeholder={String(ex.min_buying_power_pct ?? 10.0)}
              disabled={cfgSaving}
              className="w-full rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none placeholder-slate-700 disabled:opacity-50"
              style={{ background: '#0f172a', border: '1px solid #374151', fontFamily: 'JetBrains Mono, monospace' }}
            />
          </div>
        </div>
        <button
          disabled={cfgSaving || (!capEdit && !bpEdit)}
          onClick={async () => {
            const payload = {}
            if (capEdit) {
              const v = parseFloat(capEdit)
              if (isNaN(v) || v < 1 || v > 10) { setCfgMsg({ ok: false, text: 'Position cap must be 1–10%' }); return }
              payload.position_size_cap_pct = v / 100
            }
            if (bpEdit) {
              const v = parseFloat(bpEdit)
              if (isNaN(v) || v < 5 || v > 50) { setCfgMsg({ ok: false, text: 'Min buying power must be 5–50%' }); return }
              payload.min_buying_power_pct = v / 100
            }
            setCfgSaving(true); setCfgMsg(null)
            try {
              const r = await systemConfig.set(payload)
              if (r.success) {
                setCfgMsg({ ok: true, text: 'Updated — takes effect next scan cycle' })
                setCapEdit(''); setBpEdit('')
                systemConfig.get().then(d => { if (d.success) setCfg(d) }).catch(() => {})
              } else {
                setCfgMsg({ ok: false, text: r.error || 'Update failed' })
              }
            } catch(e) {
              setCfgMsg({ ok: false, text: e.message || 'Request failed' })
            } finally {
              setCfgSaving(false)
            }
          }}
          className="px-4 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-40"
          style={{
            background: '#1d4ed8', color: '#bfdbfe',
            border: '1px solid rgba(59,130,246,0.4)',
            fontFamily: 'JetBrains Mono, monospace', fontSize: '0.72rem',
          }}
        >
          {cfgSaving ? 'Saving…' : 'APPLY'}
        </button>
        {cfgMsg && (
          <p className="mt-2" style={{
            fontSize: '0.68rem', fontFamily: 'JetBrains Mono, monospace',
            color: cfgMsg.ok ? '#10b981' : '#ef4444',
          }}>
            {cfgMsg.ok ? '✓' : '✗'} {cfgMsg.text}
          </p>
        )}
      </div>
    </Section>
  )
}

// ── Schwab API Credentials section ─────────────────────────────────────────────
function SchwabCredentialsSection() {
  const [status,       setStatus]      = useState(null)
  const [appKey,       setAppKey]      = useState('')
  const [appSecret,    setAppSecret]   = useState('')
  const [redirectUri,  setRedirectUri] = useState('https://saferfunds.com/auth/callback')
  const [saving,       setSaving]      = useState(false)
  const [saveMsg,      setSaveMsg]     = useState('')
  const [testing,      setTesting]     = useState(false)
  const [testResult,   setTestResult]  = useState(null)
  const [revoking,     setRevoking]    = useState(false)

  useEffect(() => {
    schwab.status()
      .then(d => setStatus(d))
      .catch(() => setStatus({ error: 'Cannot reach backend' }))
  }, [])

  const handleSave = async () => {
    if (!appKey || !appSecret) return
    setSaving(true)
    setSaveMsg('')
    try {
      const data = await schwab.saveCredentials(appKey, appSecret, redirectUri)
      setSaveMsg(data.success ? 'Credentials saved.' : `Error: ${data.error}`)
      if (data.success) {
        setAppKey('')
        setAppSecret('')
        schwab.status().then(setStatus).catch(() => {})
      }
    } catch (e) {
      setSaveMsg(`Error: ${e.message}`)
    }
    setSaving(false)
  }

  const handleAuthorize = async () => {
    try {
      const data = await schwab.authUrl()
      if (data.success) {
        // Open as a full browser tab — more reliable than a popup for
        // Schwab's OAuth flow. window.opener still works for postMessage.
        window.open(data.auth_url, '_blank')
      } else {
        setSaveMsg(`Error: ${data.error}`)
      }
    } catch (e) {
      setSaveMsg(`Error: ${e.message}`)
    }
  }

  const handleRevoke = async () => {
    if (!window.confirm(
      'This deletes the stored OAuth token and forces a full re-authorization.\n\n' +
      'Use this when you have added "Accounts and Trading" to your Schwab app, ' +
      'or when switching to a new app key.\n\n' +
      'You will need to complete the Schwab OAuth flow again. Continue?'
    )) return
    setRevoking(true)
    setSaveMsg('')
    setTestResult(null)
    try {
      const data = await schwab.revoke()
      if (data.success) {
        setSaveMsg('Token revoked. Click RE-AUTHORIZE to complete a fresh OAuth flow.')
        schwab.status().then(setStatus).catch(() => {})
      } else {
        setSaveMsg(`Revoke failed: ${data.error || 'unknown error'}`)
      }
    } catch (e) {
      setSaveMsg(`Revoke error: ${e.message}`)
    }
    setRevoking(false)
  }

  const handleTest = async () => {
    setTesting(true)
    setTestResult(null)
    try {
      const data = await schwab.test()
      setTestResult(data)
    } catch (e) {
      setTestResult({ success: false, error: e.message })
    }
    setTesting(false)
  }

  // Listen for success message from the OAuth callback popup
  useEffect(() => {
    const handler = (event) => {
      if (event.origin !== window.location.origin) return
      if (event.data?.type === 'SCHWAB_AUTH_SUCCESS') {
        schwab.status().then(setStatus).catch(() => {})
        setSaveMsg('Schwab authorized successfully.')
      }
    }
    window.addEventListener('message', handler)
    return () => window.removeEventListener('message', handler)
  }, [])

  const isConfigured = status?.credentials_configured
  const isAuthorized = status?.token_valid

  const statusColor  = isAuthorized ? '#10b981' : isConfigured ? '#f59e0b' : '#ef4444'
  const statusLabel  = isAuthorized
    ? `CONNECTED — token expires ${status?.token_expires_at ? new Date(status.token_expires_at).toLocaleString() : '—'}`
    : isConfigured ? 'CREDENTIALS SAVED — authorization required'
    : 'NOT CONFIGURED'

  return (
    <Section title="Schwab API Credentials" icon={Key}>
      <p className="text-slate-700 -mt-2 pb-3" style={{ fontSize: '0.68rem', borderBottom: '1px solid #1e293b' }}>
        Enter your Schwab developer credentials once. They are stored in the database — no ENV variables needed.
      </p>

      {/* Connection status badge */}
      {status && (
        <div className="flex items-center gap-2">
          <div className="h-2 w-2 rounded-full flex-shrink-0" style={{ background: statusColor }} />
          <span style={{ color: statusColor, fontFamily: 'JetBrains Mono, monospace', fontSize: '0.68rem' }}>
            {statusLabel}
          </span>
        </div>
      )}

      {/* Credential inputs */}
      <div className="space-y-3">
        <div>
          <p className="text-sm text-slate-300 font-medium mb-1">App Key</p>
          <input
            type="text"
            value={appKey}
            onChange={e => setAppKey(e.target.value.trim())}
            placeholder={isConfigured ? '(saved — paste new value to update)' : 'Schwab Developer App Key'}
            className="w-full rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none placeholder-slate-700"
            style={{ background: '#0f172a', border: '1px solid #374151', fontFamily: 'JetBrains Mono, monospace', fontSize: '0.72rem' }}
          />
        </div>
        <div>
          <p className="text-sm text-slate-300 font-medium mb-1">App Secret</p>
          <input
            type="password"
            value={appSecret}
            onChange={e => setAppSecret(e.target.value.trim())}
            placeholder={isConfigured ? '(saved — paste new value to update)' : 'Schwab Developer App Secret'}
            className="w-full rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none placeholder-slate-700"
            style={{ background: '#0f172a', border: '1px solid #374151', fontFamily: 'JetBrains Mono, monospace', fontSize: '0.72rem' }}
          />
        </div>
        <div>
          <p className="text-sm text-slate-300 font-medium mb-1">Redirect URI</p>
          <input
            type="text"
            value={redirectUri}
            onChange={e => setRedirectUri(e.target.value.trim())}
            className="w-full rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none"
            style={{ background: '#0f172a', border: '1px solid #374151', fontFamily: 'JetBrains Mono, monospace', fontSize: '0.72rem' }}
          />
          <p className="text-slate-600 mt-1" style={{ fontSize: '0.62rem' }}>
            Must match exactly what is registered in your Schwab developer app.
          </p>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex gap-3 flex-wrap">
        <button
          onClick={handleSave}
          disabled={saving || (!appKey || !appSecret)}
          className="px-4 py-2 rounded-lg text-xs font-medium transition-all"
          style={{
            background: '#1d4ed8', color: '#bfdbfe',
            border: '1px solid rgba(59,130,246,0.4)',
            fontFamily: 'JetBrains Mono, monospace',
            opacity: (saving || (!appKey || !appSecret)) ? 0.4 : 1,
            cursor: (saving || (!appKey || !appSecret)) ? 'not-allowed' : 'pointer',
          }}
        >
          {saving ? 'SAVING...' : 'SAVE CREDENTIALS'}
        </button>

        {isConfigured && !isAuthorized && (
          <button
            onClick={handleAuthorize}
            className="px-4 py-2 rounded-lg text-xs font-medium transition-all"
            style={{
              background: 'rgba(16,185,129,0.1)', color: '#10b981',
              border: '1px solid rgba(16,185,129,0.3)',
              fontFamily: 'JetBrains Mono, monospace',
            }}
          >
            AUTHORIZE WITH SCHWAB
          </button>
        )}

        {isAuthorized && (
          <button
            onClick={handleAuthorize}
            className="px-4 py-2 rounded-lg text-xs font-medium transition-all"
            style={{
              background: 'rgba(30,41,59,0.5)', color: '#64748b',
              border: '1px solid #374151',
              fontFamily: 'JetBrains Mono, monospace',
            }}
          >
            RE-AUTHORIZE
          </button>
        )}

        {isConfigured && (
          <button
            onClick={handleRevoke}
            disabled={revoking}
            className="px-4 py-2 rounded-lg text-xs font-medium transition-all"
            style={{
              background: 'rgba(245,158,11,0.08)', color: '#f59e0b',
              border: '1px solid rgba(245,158,11,0.3)',
              fontFamily: 'JetBrains Mono, monospace',
              opacity: revoking ? 0.5 : 1,
              cursor: revoking ? 'not-allowed' : 'pointer',
            }}
          >
            {revoking ? 'REVOKING...' : 'REVOKE & RE-AUTHORIZE'}
          </button>
        )}
      </div>

      {isConfigured && (
        <p className="text-slate-700" style={{ fontSize: '0.62rem', fontFamily: 'JetBrains Mono, monospace' }}>
          Use <span style={{ color: '#f59e0b' }}>REVOKE &amp; RE-AUTHORIZE</span> after adding &quot;Accounts and Trading&quot; to your Schwab app, or when switching app keys. This clears the old refresh token and forces a fresh OAuth session.
        </p>
      )}

      {/* Save message */}
      {saveMsg && (
        <p className="text-xs" style={{
          color: saveMsg.startsWith('Error') ? '#ef4444' : '#10b981',
          fontFamily: 'JetBrains Mono, monospace',
        }}>
          {saveMsg}
        </p>
      )}

      {/* Live connection test */}
      {isAuthorized && (
        <div className="pt-1">
          <button
            onClick={handleTest}
            disabled={testing}
            className="px-4 py-2 rounded-lg text-xs font-medium transition-all"
            style={{
              background: 'rgba(34,211,238,0.08)', color: '#22d3ee',
              border: '1px solid rgba(34,211,238,0.2)',
              fontFamily: 'JetBrains Mono, monospace',
              opacity: testing ? 0.5 : 1,
              cursor: testing ? 'not-allowed' : 'pointer',
            }}
          >
            {testing ? 'TESTING...' : 'TEST CONNECTION'}
          </button>

          {testResult && (
            <div
              className="rounded-lg p-4 mt-3 space-y-3"
              style={{
                background: testResult.success ? 'rgba(16,185,129,0.05)' : 'rgba(244,63,94,0.05)',
                border: `1px solid ${testResult.success ? 'rgba(16,185,129,0.2)' : 'rgba(244,63,94,0.2)'}`,
              }}
            >
              {/* Status line */}
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full flex-shrink-0"
                  style={{ background: testResult.success ? '#10b981' : '#ef4444' }} />
                <span style={{
                  color: testResult.success ? '#10b981' : '#ef4444',
                  fontFamily: 'JetBrains Mono, monospace', fontSize: '0.72rem', fontWeight: 600,
                }}>
                  {testResult.success ? 'SCHWAB API REACHABLE' : 'CONNECTION FAILED'}
                </span>
              </div>

              {/* Token metadata */}
              {testResult.token_meta && (
                <div className="space-y-1">
                  {[
                    ['Authorized',             testResult.token_meta.created_at ? new Date(testResult.token_meta.created_at).toLocaleString() : '—'],
                    ['Last Token Refresh',     testResult.token_meta.updated_at ? new Date(testResult.token_meta.updated_at).toLocaleString() : '—'],
                    ['Access Token Expires',   testResult.token_meta.expires_at  ? new Date(testResult.token_meta.expires_at).toLocaleString()  : '—'],
                    ['Auto-refresh',           'Every 30 min (transparent)'],
                    ['Refresh Token Valid For', '7 days from last authorization'],
                  ].map(([label, value]) => (
                    <div key={label} className="flex justify-between">
                      <span className="text-slate-600" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.62rem' }}>{label}</span>
                      <span className="text-slate-400" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.62rem' }}>{value}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Live quote */}
              {testResult.success && testResult.live_quote && (
                <div className="pt-2" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                  <p className="text-slate-600 mb-2" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.6rem', letterSpacing: '0.1em' }}>
                    LIVE QUOTE — {testResult.live_quote.symbol}
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      ['Last',   testResult.live_quote.last_price != null ? `$${Number(testResult.live_quote.last_price).toFixed(2)}` : '—'],
                      ['Bid',    testResult.live_quote.bid   != null ? `$${Number(testResult.live_quote.bid).toFixed(2)}`   : '—'],
                      ['Ask',    testResult.live_quote.ask   != null ? `$${Number(testResult.live_quote.ask).toFixed(2)}`   : '—'],
                      ['Volume', testResult.live_quote.volume != null ? Number(testResult.live_quote.volume).toLocaleString() : '—'],
                    ].map(([label, value]) => (
                      <div key={label} className="rounded p-2" style={{ background: 'rgba(0,0,0,0.2)' }}>
                        <p className="text-slate-600" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.58rem' }}>{label}</p>
                        <p className="text-slate-200 font-bold" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.8rem' }}>{value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Error detail */}
              {!testResult.success && testResult.error && (
                <p className="text-rose-400" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.65rem' }}>
                  {testResult.error}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* First-time authorization instructions */}
      {isConfigured && !isAuthorized && (
        <div className="rounded-lg p-3" style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.15)' }}>
          <p className="text-amber-500" style={{ fontSize: '0.68rem' }}>
            <strong>Next step:</strong> Click "Authorize with Schwab". A browser popup will open — log in to your Schwab account and approve access. After approval, Schwab redirects back to saferfunds.com and the token is saved automatically. This popup step only needs to be done once (tokens auto-refresh from the DB).
          </p>
        </div>
      )}
    </Section>
  )
}

// ── Main page ──────────────────────────────────────────────────────────────────
export default function SettingsPage() {
  const { settings, update, reset } = useSettings()
  const [saved, setSaved] = useState(false)

  const handleSave = () => {
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  const pct = v => `${Math.round(v * 100)}%`

  return (
    <div className="space-y-5 max-w-2xl">

      {/* Signal display filters */}
      <Section title="Signal Display Filters" icon={Shield}>
        <p className="text-slate-700 -mt-2 pb-3" style={{ fontSize: '0.68rem', borderBottom: '1px solid #1e293b' }}>
          These filters affect which signals are shown throughout the app. They do not change the backend.
        </p>

        <Select
          label="Direction Filter"
          desc="Only show signals in this direction"
          value={settings.directionFilter}
          options={[['ALL','All (BUY + SELL)'], ['BUY','BUY only'], ['SELL','SELL only']]}
          onChange={v => update({ directionFilter: v })}
        />

        <Slider
          label="Minimum Signal Strength"
          desc="Hide signals weaker than this threshold"
          value={settings.minStrength}
          min={0} max={1} step={0.05}
          fmt={pct}
          onChange={v => update({ minStrength: v })}
        />

        <Slider
          label="Minimum Confidence"
          desc="Hide signals with confidence below this"
          value={settings.minConfidence}
          min={0} max={1} step={0.05}
          fmt={pct}
          onChange={v => update({ minConfidence: v })}
        />

        <Select
          label="Signal Lookback Window"
          desc="How far back to fetch signals"
          value={String(settings.signalHours)}
          options={[['6','6 hours'], ['12','12 hours'], ['24','24 hours (default)'], ['48','48 hours']]}
          onChange={v => update({ signalHours: Number(v) })}
        />

        {/* T-365 (owner 2026-11-04): the "Signal Types to Show" toggles were removed. They were hard-coded in March 2026 for
            11 RSI/MACD/Supertrend types that produced 0 of 2,989 signals in the 30 days to 2026-11-04 (live query), so none of
            them hid anything, and the live STOCH/MFI types had no toggle. */}
      </Section>

      {/* Display settings */}
      <Section title="Display" icon={Settings}>
        <Select
          label="Auto-refresh Interval"
          desc="How often the dashboard fetches new data from the API"
          value={String(settings.refreshInterval)}
          options={[['10','10 seconds'], ['30','30 seconds (default)'], ['60','1 minute'], ['120','2 minutes']]}
          onChange={v => update({ refreshInterval: Number(v) })}
        />

        <div>
          <p className="text-sm text-slate-300 font-medium mb-1">Default Analysis Symbol</p>
          <p className="text-slate-600 mb-2" style={{ fontSize: '0.68rem' }}>Pre-filled symbol on the Signals page</p>
          <input
            type="text"
            value={settings.defaultSymbol}
            onChange={e => update({ defaultSymbol: e.target.value.toUpperCase().slice(0, 10) })}
            placeholder="e.g. SPY"
            className="w-full rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none placeholder-slate-700"
            style={{
              background: '#0f172a',
              border: '1px solid #374151',
              fontFamily: 'JetBrains Mono, monospace',
              letterSpacing: '0.06em',
            }}
          />
        </div>
      </Section>

      {/* Schwab API Credentials */}
      <SchwabCredentialsSection />

      {/* Data Source Control */}
      <DataSourceSection />

      {/* System configuration — live from backend */}
      <LiveSystemConfigSection />

      {/* End-to-end trade flow test */}
      <TradeFlowTestSection />

      {/* API & Connection */}
      <Section title="API & Connection" icon={Database}>
        <div className="space-y-0">
          {[
            // Linux cutover 2026-11-01, Doc 50: wording no longer assumes Railway (T-323)
            ['Backend URL',    import.meta.env.VITE_API_BASE_URL || 'same origin /api (relayed to the Linux engine)'],
            // Linux cutover 2026-11-01, Doc 50: wording no longer assumes Railway (T-323)
            ['Frontend URL',   typeof window !== 'undefined' ? window.location.origin : ''],
            ['DB Type',        'TimescaleDB (PostgreSQL)'],
            ['API Framework',  'Flask (Python)'],
            ['CORS',           'Enabled via flask-cors'],
          ].map(([k, v]) => (
            <div
              key={k}
              className="flex justify-between items-center py-2.5"
              style={{ borderBottom: '1px solid rgba(30,41,59,0.5)' }}
            >
              <span className="text-slate-500" style={{ fontSize: '0.75rem' }}>{k}</span>
              <span
                className="text-slate-400"
                style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.7rem' }}
              >
                {v}
              </span>
            </div>
          ))}
        </div>
        <div
          className="rounded-lg p-3 mt-3"
          style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.15)' }}
        >
          <p className="text-amber-600" style={{ fontSize: '0.68rem' }}>
            <strong className="text-amber-500">VITE_API_BASE_URL</strong> is empty on purpose: the Dockerfile fixes it to "" so the browser calls the same-origin /api, which the relay tunnels to the Linux engine (the Railway service variable of the same name is ignored). Set it only for a build that must call a different host.
          </p>
        </div>
      </Section>

      {/* Save / Reset */}
      <div className="flex gap-3 pb-2">
        <button
          onClick={handleSave}
          className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all"
          style={{
            background: saved ? 'rgba(16,185,129,0.15)' : '#1d4ed8',
            color:      saved ? '#10b981'               : '#bfdbfe',
            border:     saved ? '1px solid rgba(16,185,129,0.3)' : '1px solid rgba(59,130,246,0.4)',
            boxShadow:  saved ? '0 0 12px rgba(16,185,129,0.2)' : '0 0 12px rgba(59,130,246,0.2)',
            fontFamily: 'JetBrains Mono, monospace',
            fontSize:   '0.72rem',
            letterSpacing: '0.06em',
          }}
        >
          <Save className="h-3.5 w-3.5" />
          {saved ? 'SAVED ✓' : 'SAVE SETTINGS'}
        </button>
        <button
          onClick={() => { reset(); setSaved(false) }}
          className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors"
          style={{
            color: '#64748b',
            border: '1px solid #374151',
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: '0.72rem',
            letterSpacing: '0.06em',
          }}
        >
          <RotateCcw className="h-3.5 w-3.5" />
          RESET DEFAULTS
        </button>
      </div>
      <p className="text-slate-700 -mt-2" style={{ fontSize: '0.65rem', fontFamily: 'JetBrains Mono, monospace' }}>
        Settings are auto-saved to localStorage and persist across reloads.
      </p>
    </div>
  )
}
